import React, { useState } from 'react';
import styled from 'styled-components';
import { Eye, EyeOff } from 'lucide-react';
import { webvonkClient } from '../lib/supabase';
import { useKoffieNav } from './KoffieApp';
import KoffieBackButton from './KoffieBackButton';

const t = {
  bg:    '#f2f0eb', white: '#ffffff', text: '#0c0c0b',
  muted: '#8a8780', lines: '#dcd9d1', accent: '#c75a0a',
  sans:  "'Jost', system-ui, sans-serif",
  serif: "'Playfair Display', Georgia, serif",
};

const Wrap = styled.div`
  height: 100%; width: 100%;
  background: ${t.bg}; overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Page = styled.div`
  padding: 36px 40px 80px;
  max-width: 480px;
  margin: 0 auto;
  @media (max-width: 600px) { padding: 24px 20px 64px; }
`;

const PageTitle = styled.h1`
  font-family: ${t.serif};
  font-size: clamp(20px, 3vw, 26px);
  font-weight: 900;
  color: ${t.text};
  letter-spacing: -0.02em;
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.muted};
  margin: 0 0 28px;
  line-height: 1.5;
`;

const FieldGroup = styled.div`margin-bottom: 16px;`;

const FieldLabel = styled.label`
  display: block;
  font-family: ${t.sans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${t.muted};
  margin-bottom: 6px;
`;

const FieldWrap = styled.div`position: relative; display: flex; align-items: center;`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 40px 10px 12px;
  background: ${t.white};
  border: 1px solid ${p => p.$error ? 'rgba(239,68,68,0.5)' : p.$success ? 'rgba(34,197,94,0.4)' : t.lines};
  border-radius: 6px;
  color: ${t.text};
  font-family: ${t.sans};
  font-size: 13px;
  &:focus { outline: none; border-color: ${t.text}; }
`;

const EyeBtn = styled.button`
  position: absolute; right: 10px;
  background: none; border: none; cursor: pointer;
  color: ${t.muted}; opacity: 0.5; padding: 0;
  display: flex; align-items: center;
  &:hover { opacity: 1; }
`;

const StrengthBars = styled.div`display: flex; gap: 3px; margin: 8px 0 4px;`;
const StrengthBar  = styled.div`
  flex: 1; height: 2px; border-radius: 2px;
  background: ${p => p.$filled ? p.$color : t.lines};
  transition: background 0.2s;
`;
const StrengthLabel = styled.div`
  font-family: ${t.sans}; font-size: 11px; color: ${p => p.$color};
`;
const ErrorList = styled.div`margin-top: 6px; display: flex; flex-direction: column; gap: 2px;`;
const ErrorItem = styled.div`font-family: ${t.sans}; font-size: 11px; color: rgba(239,68,68,0.8);`;
const MatchMsg  = styled.div`
  font-family: ${t.sans}; font-size: 11px; margin-top: 4px;
  color: ${p => p.$ok ? 'rgba(34,197,94,0.8)' : 'rgba(239,68,68,0.8)'};
`;

const SubmitBtn = styled.button`
  width: 100%; margin-top: 24px;
  padding: 11px;
  border-radius: 6px; border: none;
  background: ${p => p.disabled ? t.lines : t.text};
  color: ${p => p.disabled ? t.muted : t.bg};
  font-family: ${t.sans}; font-size: 13px; font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
`;

const Feedback = styled.div`
  margin-top: 12px; padding: 10px 14px; border-radius: 6px;
  font-family: ${t.sans}; font-size: 12px;
  background: ${p => p.$success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'};
  color: ${p => p.$success ? '#22c55e' : '#ef4444'};
  border: 1px solid ${p => p.$success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'};
`;

const COMMON = ['123456','password','123456789','12345678','12345','1234567','qwerty','abc123','111111','123123','admin','letmein','welcome','monkey','dragon','master','sunshine','princess','iloveyou','password1'];
const STRENGTH_COLORS = ['#ef4444','#ef4444','#f97316','#eab308','#22c55e'];
const STRENGTH_LABELS = ['','Zwak','Zwak','Gemiddeld','Sterk','Zeer sterk'];

function getStrength(pw) {
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[!@#$%^&*]/.test(pw)) s++;
  return s;
}

function getErrors(pw, current, username) {
  const errs = [];
  if (pw.length < 8) errs.push('Minimaal 8 tekens');
  if (pw.includes(' ')) errs.push('Geen spaties');
  if (!/[A-Z]/.test(pw)) errs.push('Minimaal 1 hoofdletter');
  if (!/[a-z]/.test(pw)) errs.push('Minimaal 1 kleine letter');
  if (!/[0-9]/.test(pw)) errs.push('Minimaal 1 cijfer');
  if (!/[!@#$%^&*]/.test(pw)) errs.push('Minimaal 1 speciaal teken (!@#$%^&*)');
  if (pw === current) errs.push('Niet gelijk aan huidig wachtwoord');
  if (username && pw.toLowerCase() === username.toLowerCase()) errs.push('Niet gelijk aan je gebruikersnaam');
  if (COMMON.includes(pw.toLowerCase())) errs.push('Geen veelgebruikt wachtwoord');
  return errs;
}

export default function KoffieChangePassword({ user }) {
  const { handleNavigation } = useKoffieNav();
  const username = user?.username || '';
  const normalizedUsername = username.normalize('NFD').replace(/[̀-ͯ]/g, '');

  const [current, setCurrent]         = useState('');
  const [newPw, setNewPw]             = useState('');
  const [confirm, setConfirm]         = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [feedback, setFeedback]       = useState(null);

  const errors   = newPw ? getErrors(newPw, current, username) : [];
  const strength = getStrength(newPw);
  const color    = STRENGTH_COLORS[strength] ?? '#22c55e';
  const isValid  = current && !errors.length && confirm === newPw && newPw !== current;

  const handleSubmit = async () => {
    if (!isValid || loading) return;
    setLoading(true); setFeedback(null);
    try {
      const email = `${normalizedUsername}@webvonk.app`;
      const { error: signInError } = await webvonkClient.auth.signInWithPassword({ email, password: current });
      if (signInError) { setFeedback({ msg: 'Huidig wachtwoord is onjuist.', success: false }); setLoading(false); return; }
      const { error: updateError } = await webvonkClient.auth.updateUser({ password: newPw });
      if (updateError) throw updateError;
      setFeedback({ msg: 'Wachtwoord succesvol gewijzigd.', success: true });
      setCurrent(''); setNewPw(''); setConfirm('');
    } catch {
      setFeedback({ msg: 'Er is iets misgegaan, neem contact met ons op.', success: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrap>
      <Page>
        <KoffieBackButton to="/Koffiereparatie/settings" />
        <PageTitle>Wachtwoord wijzigen</PageTitle>
        <PageSub>Voer je huidige wachtwoord in en kies een nieuw wachtwoord.</PageSub>

        <FieldGroup>
          <FieldLabel>Huidig wachtwoord</FieldLabel>
          <FieldWrap>
            <Input type={showCurrent ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)} autoComplete="current-password" />
            <EyeBtn type="button" onClick={() => setShowCurrent(v => !v)} tabIndex={-1}>
              {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
            </EyeBtn>
          </FieldWrap>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Nieuw wachtwoord</FieldLabel>
          <FieldWrap>
            <Input type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} $error={newPw && errors.length > 0} $success={newPw && errors.length === 0} autoComplete="new-password" />
            <EyeBtn type="button" onClick={() => setShowNew(v => !v)} tabIndex={-1}>
              {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
            </EyeBtn>
          </FieldWrap>
          {newPw && (
            <>
              <StrengthBars>
                {[1,2,3,4].map(i => <StrengthBar key={i} $filled={strength >= i} $color={color} />)}
              </StrengthBars>
              <StrengthLabel $color={color}>{STRENGTH_LABELS[strength]}</StrengthLabel>
              {errors.length > 0 && <ErrorList>{errors.map(e => <ErrorItem key={e}>· {e}</ErrorItem>)}</ErrorList>}
            </>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Bevestig nieuw wachtwoord</FieldLabel>
          <FieldWrap>
            <Input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)} $error={confirm && confirm !== newPw} $success={confirm && confirm === newPw} autoComplete="new-password" />
            <EyeBtn type="button" onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
              {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
            </EyeBtn>
          </FieldWrap>
          {confirm && <MatchMsg $ok={confirm === newPw}>{confirm === newPw ? 'Wachtwoorden komen overeen' : 'Wachtwoorden komen niet overeen'}</MatchMsg>}
        </FieldGroup>

        <SubmitBtn onClick={handleSubmit} disabled={!isValid || loading}>
          {loading ? 'Bezig…' : 'Wachtwoord wijzigen'}
        </SubmitBtn>

        {feedback && <Feedback $success={feedback.success}>{feedback.msg}</Feedback>}
      </Page>
    </Wrap>
  );
}
